import { useTranslation } from 'react-i18next';
import React, { useRef, useState, useEffect, ReactNode } from 'react';
import cx from 'clsx';

import { IconRcTick } from '@/assets';
import { IconRcActionRefresh } from '@/assets/stream';
import { HelperTooltip, Popup } from '@/components';
import type { FeedQuestAction } from '@/views/Stream/types';
import { openInDebank, login } from '@/utils';
import {
  useArticleQuestActionVisit,
  useArticleQuestActionVerify,
} from '@/views/Stream/hooks/quest';
import {
  useCountdown,
  useCurrentAddressInfo,
  useDeviceTypeIsMobile,
  useQueryParams,
} from '@/hooks';

import style from './index.module.scss';

export type ActionProps = {
  articleId: number;
  data: FeedQuestAction;
  disableTip?: ReactNode;
  isCompleted: boolean;
};

const ActionTooltipAlign = {
  targetOffset: [100, 0] as [number, number],
};

export const ActionWrapper = ({
  articleId,
  data,
  disableTip,
  isCompleted,
  onActionClick,
  children,
  avatar,
  disabledFetchVisit,
}: ActionProps & {
  children?: ReactNode;
  avatar?: ReactNode;
  onActionClick?: () => void;
  disabledFetchVisit?: boolean; // 禁止发送/quest/action/visit_url请求
}) => {
  const { t } = useTranslation();
  const [refCode] = useQueryParams<string>('r');
  const [privateToken] = useQueryParams<string>('private_token');

  const iconRef = useRef<SVGSVGElement>(null);
  const animateRef = useRef<Animation>();
  const isMobile = useDeviceTypeIsMobile();

  const { isVerified } = useCurrentAddressInfo();
  const [error, setError] = useState('');
  const errorTimer = useRef<NodeJS.Timer | null>(null);
  const { seconds, startCountdown } = useCountdown(60);

  const { mutate: visitLink } = useArticleQuestActionVisit(data.id);
  const { mutateAsync: verifyAction } = useArticleQuestActionVerify({
    id: articleId,
    action_id: data.id,
    refCode,
    privateToken,
  });

  const needLogin = () => {
    if (isMobile) {
      openInDebank({ link: `/stream/${articleId}` });
      return true;
    }

    if (!isVerified) {
      login();
      return true;
    }
  };

  const handleActionClick = async () => {
    if (needLogin()) {
      return;
    }

    if (disableTip || !data.data) {
      return;
    }

    if (!data.is_verified && !disabledFetchVisit) {
      await visitLink();
    }

    onActionClick?.();
  };

  const handleVerifyClick = async () => {
    if (needLogin() || seconds) {
      return;
    }

    try {
      if (errorTimer.current) {
        clearTimeout(errorTimer.current);
        errorTimer.current = null;
      }
      // 试试新的 Web Animation API，不占用主线程，也不会触发 style 改变，再次 cssom 构建计算
      animateRef.current = iconRef.current?.animate([{ rotate: '0deg' }, { rotate: '360deg' }], {
        duration: 1000,
        fill: 'forwards',
        iterations: Infinity,
      });
      await verifyAction();
    } catch (error: any) {
      setError(error.error_msg);
      startCountdown();
      errorTimer.current = setTimeout(() => {
        setError('');
        errorTimer.current = null;
      }, 5000);
    }
  };

  useEffect(() => {
    if (seconds === 0 && animateRef.current?.playState === 'running') {
      animateRef.current.cancel();
      animateRef.current = undefined;
    }
  }, [seconds]);

  useEffect(() => {
    if (errorTimer.current) {
      clearTimeout(errorTimer.current);
    }
  }, []);

  return (
    <Popup
      open={!!error}
      position="top right"
      arrow={false}
      on={false}
      className="questActionPopup"
      trigger={
        <div className={cx(style.action, !data.is_verified && disableTip && style.isClosed)}>
          {avatar}
          <HelperTooltip
            disable={!disableTip || data.is_verified}
            title={disableTip}
            align={ActionTooltipAlign}>
            <div
              className={cx(
                style.actionContent,
                !disableTip && !data.is_verified && style.isActive,
              )}
              onClick={handleActionClick}>
              {children}
            </div>
          </HelperTooltip>
          {data.is_verified ? (
            <div className={cx(style.stateContainer, style.verified)}>
              <IconRcTick className={style.completeIcon} />
              {data?.unit_xp} XP
            </div>
          ) : disableTip ? null : (
            <HelperTooltip
              placement="bottom"
              disable={isCompleted || data.is_verified}
              title={
                <span className={style.tip}>
                  {t('QuestCard_Task.1wrw05')}
                  {'\n'}
                  <span className={style.tipBold}>{t('QuestCard_Task.sd5pbc')}</span>
                </span>
              }>
              {
                <div
                  className={cx(
                    style.stateContainer,
                    // 让动画有个一秒时间
                    seconds && seconds !== 60 && style.isCountDown,
                  )}
                  onClick={handleVerifyClick}>
                  <IconRcActionRefresh className={style.refreshIcon} ref={iconRef} />
                  {seconds && seconds !== 60 ? (
                    <div className={style.countdown}>{seconds}</div>
                  ) : (
                    `${data?.unit_xp} XP`
                  )}
                </div>
              }
            </HelperTooltip>
          )}
        </div>
      }>
      <div className={style.errorTitle}>{t('QuestCard_Task.1v22zl8')}</div>
      <div className={style.errorDesc}>{error}</div>
    </Popup>
  );
};
